import axios from "axios";
import config from "../../config/config";
import * as mocks from "./module.mocks";
import {
  Module,
  CreateModuleRequest,
  UpdateModuleRequest,
  ModuleInfo,
} from "./module.interfaces";
import { PackageModuleInfo } from "./packages.interfaces";

const API_URL = `${config.apiBaseUrl}/students/modules`;

const apiGetModules = async (): Promise<Module[]> => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get<Module[]>(API_URL, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch modules:", error);
    throw error;
  }
};

const apiGetModule = async (id: string): Promise<Module> => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get<Module>(`${API_URL}/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch module ${id}:`, error);
    throw error;
  }
};

const apiGetModuleInfo = async (id: string): Promise<ModuleInfo> => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get<ModuleInfo>(`${API_URL}/${id}/info`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch module info for ${id}:`, error);
    throw error;
  }
};

const apiCreateModule = async (
  module: CreateModuleRequest
): Promise<Module> => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.post<Module>(API_URL, module, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to create module:", error);
    throw error;
  }
};

const apiUpdateModule = async (
  id: string,
  module: UpdateModuleRequest
): Promise<Module> => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.put<Module>(`${API_URL}/${id}`, module, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to update module ${id}:`, error);
    throw error;
  }
};

const apiDeleteModule = async (id: string): Promise<void> => {
  try {
    const token = localStorage.getItem("token");
    await axios.delete(`${API_URL}/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    console.error(`Failed to delete module ${id}:`, error);
    throw error;
  }
};

export const getModules = (): Promise<Module[]> => {
  return config.useRealApi ? apiGetModules() : mocks.getModules();
};

export const getModule = (id: string): Promise<Module> => {
  return config.useRealApi ? apiGetModule(id) : mocks.getModule(id);
};

export const getModuleInfo = (id: string): Promise<ModuleInfo> => {
  return config.useRealApi ? apiGetModuleInfo(id) : mocks.getModuleInfo(id);
};

export const createModule = (module: CreateModuleRequest): Promise<Module> => {
  return config.useRealApi
    ? apiCreateModule(module)
    : mocks.createModule(module);
};

export const updateModule = (
  id: string,
  module: UpdateModuleRequest
): Promise<Module> => {
  return config.useRealApi
    ? apiUpdateModule(id, module)
    : mocks.updateModule(id, module);
};

export const deleteModule = (id: string): Promise<void> => {
  return config.useRealApi ? apiDeleteModule(id) : mocks.deleteModule(id);
};

// Nova função para obter todos os módulos sem depender de packageId
export const getAllModules = async (): Promise<PackageModuleInfo[]> => {
  try {
    const modules = await getModules();

    // Converter o formato Module para PackageModuleInfo
    return modules.map((module, index) => ({
      ...module,
      description: module.description || "",
      title: module.title,
      teaser: module.teaser,
      summarizedDescription: module.summarizedDescription,
      order: module.order || index + 1,
      state:
        (module.state as
          | "not_available"
          | "available"
          | "in-progress"
          | "completed"
          | "needs_purchase") || "not_available",
      purchasePackageId: module.purchasePackageId,
    }));
  } catch (error) {
    console.error("Error fetching all modules:", error);
    throw error;
  }
};
